import React, { useState, useEffect } from 'react';
import Dropdown from 'antd/lib/dropdown';
import TimeIcon from '../../../assets/svg/time-zone.svg';
import ArrowIcon from '../../../assets/svg/arrow-down.svg';
import { DropDownBox } from '../../../components/dropdown-box';
import moment from 'moment-timezone';
import { Button, Input } from 'antd';
import getFormatMessage from '../../../locales/get-format-message';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from '../../../store/reducers';
import inputSearch from '../../../assets/img/input-search.png';
import { userTimeZoneSetting } from '../../../api/time-zone';
import { getUserInfo } from '../../../api';
import {
  CommonAction,
  ReduxCommonActionType,
} from '../../../store/modules/common';
import { Dispatch } from 'redux';

interface ITimezoneData {
  value: string;
  label: string;
  cityName: string;
  utcOffset: string;
  countryCode?: string;
}

export const ItemTimeSwitchComponent = () => {
  const [allTimezones, setAllTimezones] = useState<ITimezoneData[]>([]);
  const [currentSelectedTimezone, setCurrentSelectedTimezone] =
    useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [filteredTimezones, setFilteredTimezones] = useState<ITimezoneData[]>(
    []
  );
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);
  const userDispatch = useDispatch<Dispatch<CommonAction>>();
  const defaultTimezone = moment.tz.guess();

  useEffect(() => {
    setCurrentSelectedTimezone(
      userInfo?.UserSettings?.Timezone || defaultTimezone
    );
  }, [userInfo]);

  // 小写去空格
  const toLowerCaseAndRemoveSpaces = (str: string) => {
    return str.toLowerCase().replace(/\s+/g, '');
  };

  useEffect(() => {
    const timezones: string[] = moment.tz.names();
    const processedTimezones: ITimezoneData[] = [];

    timezones.forEach((tz: string) => {
      const offset: string = moment().tz(tz).format('Z');
      const parts: string[] = tz.split('/');
      const region: string = parts[0];
      let cityName: string = parts[parts.length - 1].replace(/_/g, ' ');

      cityName = cityName
        .split(' ')
        .map(
          (word: string) =>
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(' ');

      // 生成格式为 "(UTC+08:00) Shanghai" 的标签
      const displayLabel = `(UTC${offset}) ${cityName}`;

      const timezoneItem: ITimezoneData = {
        value: tz,
        label: displayLabel, // 使用新的显示格式
        cityName,
        utcOffset: offset,
        countryCode: region,
      };
      const timeZoneIt = processedTimezones.find(
        (item) =>
          toLowerCaseAndRemoveSpaces(item.label) ===
          toLowerCaseAndRemoveSpaces(displayLabel)
      );
      if (!timeZoneIt) {
        processedTimezones.push(timezoneItem);
      }
    });

    // 按 UTC 偏移量排序
    processedTimezones.sort((a: ITimezoneData, b: ITimezoneData) => {
      const offsetA: number = parseInt(a.utcOffset.replace(':', ''));
      const offsetB: number = parseInt(b.utcOffset.replace(':', ''));
      return offsetA - offsetB;
    });

    setAllTimezones(processedTimezones);
  }, []);

  useEffect(() => {
    if (!searchKeyword.trim()) {
      setFilteredTimezones(allTimezones);
      return;
    }

    const filtered = allTimezones.filter((tz: ITimezoneData) => {
      const keyword = searchKeyword.toLowerCase();
      return (
        tz.cityName.toLowerCase().includes(keyword) ||
        tz.label.toLowerCase().includes(keyword) ||
        tz.value.toLowerCase().includes(keyword) ||
        tz.utcOffset.toLowerCase().includes(keyword)
      );
    });

    setFilteredTimezones(filtered);
  }, [searchKeyword, allTimezones]);

  const handleTimezoneSelect = (timezoneValue: string): void => {
    setCurrentSelectedTimezone(timezoneValue);
  };

  const handleTimezoneConfirm = async () => {
    setIsDropdownOpen(false);
    setSearchKeyword('');
    const result = await userTimeZoneSetting(currentSelectedTimezone);
    if (result) {
      const res = await getUserInfo();
      if (res) {
        userDispatch({
          type: ReduxCommonActionType.LOGIN,
          result: res,
        });
      }
    }
  };

  const handleTimezoneCancel = () => {
    setCurrentSelectedTimezone(
      userInfo?.UserSettings?.Timezone || defaultTimezone
    );
    setIsDropdownOpen(false);
    setSearchKeyword('');
  };

  const handleOpenChange = (open: boolean) => {
    setIsDropdownOpen(open);

    if (!open) {
      // 下拉框关闭时的逻辑
      handleTimezoneCancel();

      // 清空搜索关键词
      setSearchKeyword('');
    } else {
      // 下拉框打开时的逻辑
      console.log('Dropdown opened');
    }
  };

  const renderTimezoneOption = (tz: ITimezoneData): JSX.Element => (
    <div
      key={tz.value}
      onClick={() => handleTimezoneSelect(tz.value)}
      onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
        e.currentTarget.style.backgroundColor = '#f8f9fa';
      }}
      onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
        e.currentTarget.style.backgroundColor = 'white';
      }}
      style={{
        color: tz.value === currentSelectedTimezone ? '#5764F1' : '#222222',
      }}
      className="switch-choice-body-item"
    >
      <span>{tz.label}</span>
    </div>
  );

  const items = (): JSX.Element => {
    return (
      <div className="time-switch-choice">
        <div className="switch-choice-title">
          <div className="switch-choice-title-top">
            {getFormatMessage('time_zone_setting')}
          </div>
          <div className="switch-choice-title-bottom">
            {getFormatMessage('time_zone_setting_desc')}
          </div>
        </div>
        <div className="switch-choice-search">
          <Input
            type=""
            placeholder={getFormatMessage('please_enter_city')}
            value={searchKeyword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchKeyword(e.target.value)
            }
            style={{
              borderRadius: '2px',
              fontSize: '14px',
            }}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            className="search-input"
            prefix={
              <img
                src={inputSearch}
                //   className={classNames('icon', styles.inputSearch)}
              />
            }
          />
        </div>
        <div className="switch-choice-body">
          {filteredTimezones.map(renderTimezoneOption)}
          {filteredTimezones.length === 0 && (
            <div
              style={{
                padding: '20px',
                textAlign: 'center',
                color: '#999',
                fontSize: '14px',
              }}
            >
              {getFormatMessage('no_search_result')}
            </div>
          )}
        </div>
        <div className="switch-choice-btn">
          <Button
            className="gc-btn gc-btn-primary switch-choice-btn-item"
            onClick={handleTimezoneConfirm}
          >
            {getFormatMessage('confirm')}
          </Button>
          <Button
            className="gc-btn gc-btn-default switch-choice-btn-item"
            onClick={handleTimezoneCancel}
          >
            {getFormatMessage('cancel')}
          </Button>
        </div>
      </div>
    );
  };

  const renderTimeText = () => {
    const selectedTimezone =
      userInfo?.UserSettings?.Timezone || defaultTimezone;
    const current: ITimezoneData | undefined = allTimezones.find(
      (tz: ITimezoneData) => tz.value === selectedTimezone
    );
    return (
      <div style={{ display: 'flex' }}>
        <div style={{ fontSize: '14px', color: '#1D2129', marginTop: '-1px' }}>
          {current?.label || ''}
        </div>
        <img
          src={ArrowIcon}
          style={{
            width: '9px',
            marginLeft: '4px',
            marginTop: '2px',
            transform: isDropdownOpen ? 'rotate(180deg)' : '',
          }}
        />
      </div>
    );
  };

  return (
    <li>
      <Dropdown
        overlay={items()}
        destroyPopupOnHide={true}
        overlayClassName="site-dropdown-bottom"
        trigger={['click']}
        arrow={true}
        visible={isDropdownOpen}
        onVisibleChange={handleOpenChange}
      >
        <a className="action-account">
          <DropDownBox
            className="drop-down-box drop-down-time-box"
            hasArrow={false}
            fontSize={16}
            icon={
              <span className="account-icon">
                <img src={TimeIcon} style={{ width: 16, height: 16 }} />
              </span>
            }
            title={renderTimeText()} // 显示当前选择的时区标签
          />
        </a>
      </Dropdown>
    </li>
  );
};
